import { useEffect, useRef, useState } from "react";
import useAuth from "../useAuth";
import useWebotServer from "../useWebotServer";
import { contactLookupStore } from "./contactLookupStore";
import { rowToContactSummary } from "./contactSummary";
import type { ContactSummary } from "./contactSummary";

/**
 * "FIND ME A CONTACT" — the options behind a contact picker, searched on the server.
 *
 * ── What it replaces ────────────────────────────────────────────────────────────────
 *
 * A picker whose options were the whole contact book of the caller: `getAllContacts()` to
 * download it (seconds, megabytes, up to 92,069 contacts), then a substring filter over the
 * array in the browser, capped at 100 results. The server answers the same question from
 * an index — name, phone in any spelling, tag — and returns the few dozen rows a menu can
 * show.
 *
 * An empty term returns the most recently touched contacts, which is what a realtor opening
 * a picker without typing is most likely looking for.
 *
 * ── Behaviour ───────────────────────────────────────────────────────────────────────
 *
 *  - Debounced: typing a name is one request, not one per keystroke.
 *  - The previous results stay on screen while the next ones load, so the menu does not
 *    blink empty between keystrokes; only the answer to the newest request is ever shown.
 *  - `enabled: false` (the picker is closed) asks nothing and keeps what it has.
 *  - Every row that comes back is written through to the lookup store, so a name seen in a
 *    picker is already known to the chip, the card and the table that show it next.
 *
 * Results are SUMMARIES — enough to draw an option. A picker that hands the chosen contact
 * to a form reads its document first; see `NewContactPicker`.
 */

const DEBOUNCE_MS = 250;
const DEFAULT_LIMIT = 30;

export type ContactSearchState = {
  results: ContactSummary[];
  /** True from the moment the term changes until its answer (or failure) lands. */
  loading: boolean;
  /** The last request failed. `results` still holds the previous answer. */
  failed: boolean;
};

const IDLE: ContactSearchState = { results: [], loading: false, failed: false };

export default function useContactSearch(
  term: string,
  options: { enabled?: boolean; limit?: number } = {}
): ContactSearchState {
  const { contactsList } = useWebotServer();
  const { user } = useAuth();
  const account = ((user?.email as string | undefined) ?? "").trim().toLowerCase();
  const enabled = (options.enabled ?? true) && Boolean(account);
  const limit = options.limit ?? DEFAULT_LIMIT;
  const needle = term.trim();

  // `useWebotServer()` rebuilds its functions on every render.
  const fetcher = useRef(contactsList);
  fetcher.current = contactsList;

  const [state, setState] = useState<ContactSearchState>(IDLE);

  // A sign-in as someone else must not show the contacts of the previous account.
  useEffect(() => setState(IDLE), [account]);

  useEffect(() => {
    if (!enabled) return undefined;

    let superseded = false;
    setState((current) => ({ ...current, loading: true }));

    const timer = setTimeout(
      () => {
        fetcher
          .current({ view: "mine", search: needle || undefined, limit, sortBy: "updatedTime", sortDir: "desc" })
          .then((response) => {
            if (superseded) return;
            const results = response.contacts.map(rowToContactSummary);
            contactLookupStore.bindAccount(account);
            contactLookupStore.putMany(results);
            setState({ results, loading: false, failed: false });
          })
          .catch((error) => {
            if (superseded) return;
            // eslint-disable-next-line no-console
            console.error("useContactSearch: the contact search failed", error);
            setState((current) => ({ ...current, loading: false, failed: true }));
          });
      },
      // Nothing typed is not a keystroke to wait out: show the recent contacts at once.
      needle ? DEBOUNCE_MS : 0
    );

    return () => {
      superseded = true;
      clearTimeout(timer);
    };
  }, [enabled, account, needle, limit]);

  return state;
}
